export type RegionScope = { permission: string; regionCode: string }

export type TokenClaims = {
  sub?: string
  user_id?: string
  name?: string
  super_admin?: boolean
  /** 生产端（Principal）签发使用 camelCase，与 snake_case 二选一 */
  superAdmin?: boolean
  permissions?: string[]
  region_scopes?: RegionScope[]
  regionScopes?: RegionScope[]
  iss?: string
  exp?: number
}

/** 宽松解析 JWT payload（仅用于前端 UI gate，服务端仍会校验） */
export function parseTokenClaims(token: string | null | undefined): TokenClaims | null {
  if (!token) return null
  const parts = token.split('.')
  if (parts.length !== 3) return null
  try {
    const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(json) as TokenClaims
  } catch {
    return null
  }
}

export function isSuperAdmin(claims: TokenClaims | null): boolean {
  return claims?.super_admin === true || claims?.superAdmin === true
}

/** super_admin 全通过；否则要求权限码精确匹配 */
export function hasPermission(claims: TokenClaims | null, permission: string): boolean {
  if (!claims) return false
  if (isSuperAdmin(claims)) return true
  return claims.permissions?.includes(permission) ?? false
}

/** 任一权限码命中即通过（super_admin 全通过） */
export function hasAnyPermission(claims: TokenClaims | null, permissions: string[]): boolean {
  return permissions.some((permission) => hasPermission(claims, permission))
}

/** 非 super_admin 必须提供 regionCode；scope regionCode 为 "*" 表示全局 */
export function hasRegionScope(claims: TokenClaims | null, permission: string, regionCode: string): boolean {
  if (!claims) return false
  if (isSuperAdmin(claims)) return true
  return (
    (claims.region_scopes ?? claims.regionScopes)?.some(
      (scope) => scope.permission === permission && (scope.regionCode === '*' || scope.regionCode === regionCode),
    ) ?? false
  )
}

/** exp 是否已过期（含 skew 秒钟的时钟偏移容差）；无 exp 视为未过期（由服务端裁决） */
export function isTokenExpired(token: string | null | undefined, skewSec = 30): boolean {
  const claims = parseTokenClaims(token)
  if (!claims?.exp) return false
  return claims.exp * 1000 <= Date.now() + skewSec * 1000
}
